// SR19 — the mouth-to-ear budget, as arithmetic rather than as a picture.
//
// The epic's `index.md` states a latency budget as a table of named stages with a target each, and
// a headline: a spoken reply must start within 800 ms of the caller falling silent. This module
// turns the two measurement sources the dashboard can actually reach into that table, and answers
// the only question the table exists to answer — is the target met, and if not, WHICH row spent the
// milliseconds.
//
// Two rules run through everything here, and both are about honesty rather than about arithmetic:
//
//   1. A stage nobody measured reads as "not measured", never as zero. A zero in a latency budget
//      is the most expensive kind of wrong: it is indistinguishable from a fast stage, so it moves
//      the blame onto whichever row DID report — which is exactly the row somebody would then go
//      and optimise. Every row therefore carries its sample count, and a row with no samples says
//      so in words.
//   2. The verdict is computed from the MEASURED end-to-end number, not from the sum of the parts.
//      Summing the stages would quietly hide the un-instrumented ones (mic capture, the browser's
//      jitter buffer) and produce a total that beats a target the caller can hear it missing.
//
// The stage names are the ones the assistant's voice metric family already emits
// (`GET /api/v1/metrics/summary` → `voice.stages`), so this file adds no new vocabulary: it maps
// them onto the rows of the budget table and states each row's target.

/** One duration stat as the assistant's metric family reports it (`_dur_stats`). */
export interface DurStat {
  count: number;
  p50_ms: number;
  p95_ms: number;
  max_ms: number;
}

/** The `voice` block of the assistant's metrics summary, reduced to what a budget needs. */
export interface VoiceStages {
  stages: Record<string, DurStat | undefined>;
  mouth_to_ear_by_asr?: { asr: string; count: number; p50_ms: number; p95_ms: number }[];
  targets?: {
    ptt_p50_s?: number;
    realtime_p50_s?: number;
    ptt?: { within: number; total: number };
    realtime?: { within: number; total: number };
  };
}

/** The per-hop latency the gateway measures on its own wire (SR14). */
export interface HopLatency {
  latency_p50_ms: number;
  latency_p95_ms: number;
  latency_samples: number;
}

/** Where a row's number came from — rendered, because the two clocks are not the same clock. */
export type BudgetSource = 'assistant' | 'gateway' | 'uninstrumented';

export interface BudgetRow {
  id: string;
  /** The row as the epic's budget table names it. */
  label: string;
  /** The upper end of that row's stated budget, in ms. */
  targetMs: number;
  source: BudgetSource;
  p50Ms: number | null;
  p95Ms: number | null;
  samples: number;
  /** Why there is no number, or what the number actually spans. Always worth reading. */
  note?: string;
}

/** SR19's headline budget: end of speech → first reply audio, p50, in ms. */
export const MOUTH_TO_EAR_TARGET_MS = 800;

/** The voice-realtime epic's push-to-talk budget, kept for the side-by-side comparison. */
export const PTT_TARGET_MS = 2500;

/** The ASR label the streaming call route's samples carry; the push-to-talk path uses `chunked`. */
export const REALTIME_ASR = 'duplex';
export const PTT_ASR = 'chunked';

const EMPTY: DurStat = { count: 0, p50_ms: 0, p95_ms: 0, max_ms: 0 };

/** A stat with no samples is NOT a fast stage — read it as absent, so the row can say so. */
function stat(voice: VoiceStages | null | undefined, stage: string): DurStat {
  const s = voice?.stages?.[stage];
  return s && s.count > 0 ? s : EMPTY;
}

function row(
  id: string,
  label: string,
  targetMs: number,
  source: BudgetSource,
  s: DurStat,
  note?: string,
): BudgetRow {
  return {
    id,
    label,
    targetMs,
    source,
    p50Ms: s.count > 0 ? s.p50_ms : null,
    p95Ms: s.count > 0 ? s.p95_ms : null,
    samples: s.count,
    note,
  };
}

/**
 * The worst hop of a set of route instances — the gateway's own contribution to the chain.
 *
 * WORST, not the mean: the budget row asks what one frame pays crossing the gateway, and a chain is
 * as slow as its slowest link. Averaging four hops of which one stalls produces a number that no
 * frame ever experienced, and the stalling hop is the finding.
 */
export function worstHopLatency(hops: (HopLatency | null | undefined)[]): DurStat {
  let p50 = 0;
  let p95 = 0;
  let samples = 0;
  for (const h of hops) {
    if (!h || !h.latency_samples) continue;
    samples += h.latency_samples;
    p50 = Math.max(p50, h.latency_p50_ms);
    p95 = Math.max(p95, h.latency_p95_ms);
  }
  return { count: samples, p50_ms: p50, p95_ms: p95, max_ms: p95 };
}

/**
 * The budget table for one window: every row of `index.md`, with the number if somebody measured it.
 *
 * The two un-instrumented rows are deliberately kept IN the table rather than dropped. A budget that
 * silently omits the stages nobody timed reads as complete, and the first question a reader of this
 * panel has after "is it slow" is "is this all of it" — a row that says "not measured" answers that;
 * a missing row answers it wrongly.
 */
export function buildBudget(voice: VoiceStages | null, hops: DurStat): BudgetRow[] {
  return [
    row(
      'mic_ws',
      'Mic capture → UP frame',
      40,
      'uninstrumented',
      EMPTY,
      'Measured only by the browser that holds the microphone; the call path posts no client clock.',
    ),
    row(
      'vad_eos',
      'VAD end-of-speech (hangover)',
      250,
      'uninstrumented',
      EMPTY,
      'Folded into the next row by construction: the sidecar’s end-of-speech mark is already ' +
        'hangover-corrected, so the hangover is paid inside "end of speech → final transcript".',
    ),
    row(
      'stt_final',
      'End of speech → final transcript',
      200,
      'assistant',
      stat(voice, 'eos_to_final'),
      'The STT hop plus the UP leg, from the VAD’s corrected end-of-speech to the transcript ' +
        'reaching the assistant hop.',
    ),
    row(
      'llm_first_token',
      'LLM first token',
      350,
      'assistant',
      stat(voice, 'llm_first_token'),
      'Request out of the assistant hop → the model’s first token back.',
    ),
    row(
      'gateway_hop',
      'Gateway hop → hop',
      15,
      'gateway',
      hops,
      'The gateway’s own queue-and-write share, worst hop of the window (SR14).',
    ),
    row(
      'tts_first_audio',
      'TTS first audio',
      300,
      'assistant',
      stat(voice, 'tts'),
      'Recorded by the push-to-talk path; the streaming TTS hop of the call route is timed only ' +
        'end-to-end, inside mouth-to-ear.',
    ),
    row(
      'down_speaker',
      'DOWN → speaker (jitter buffer)',
      60,
      'uninstrumented',
      EMPTY,
      'Below the gateway’s last frame: what the browser’s player buffers before it makes a sound.',
    ),
  ];
}

/**
 * The measured prefix of the budget — end of speech to the moment reply TEXT exists.
 *
 * Reported separately from the rows it spans because it is measured directly rather than summed:
 * it is one subtraction of two marks on one clock, and it is the half of the budget this stand can
 * currently prove. Its target is the sum of the two rows it covers.
 */
export function replyTextRow(voice: VoiceStages | null): BudgetRow {
  return row(
    'eos_to_reply_text',
    'End of speech → reply text ready',
    550,
    'assistant',
    stat(voice, 'eos_to_reply_text'),
    'Measured end to end on the assistant hop, not summed from the rows above.',
  );
}

export interface BudgetOffender {
  id: string;
  label: string;
  p50Ms: number;
  targetMs: number;
  overMs: number;
}

export interface BudgetVerdict {
  /** The measured end-to-end p50, or null when nothing was measured in this window. */
  p50Ms: number | null;
  p95Ms: number | null;
  targetMs: number;
  samples: number;
  /** `null` = no opinion, because nothing was measured. Never defaults to "met". */
  met: boolean | null;
  /** How far past the target the measured p50 landed. */
  overMs: number | null;
  /** The rows that are over their OWN budget, worst first — where the milliseconds went. */
  offenders: BudgetOffender[];
}

/**
 * Is the target met, and if not, which rows spent it.
 *
 * `met` is tri-state on purpose. A panel that renders "target met" because the window was empty is
 * worse than one that renders nothing: it is a green light nobody earned, and this metric exists
 * precisely so an optimisation can be compared against a number that was really taken.
 */
export function budgetVerdict(
  endToEnd: DurStat,
  rows: BudgetRow[],
  targetMs = MOUTH_TO_EAR_TARGET_MS,
): BudgetVerdict {
  const offenders = rows
    .filter((r) => r.p50Ms !== null && r.p50Ms > r.targetMs)
    .map((r) => ({
      id: r.id,
      label: r.label,
      p50Ms: r.p50Ms as number,
      targetMs: r.targetMs,
      overMs: (r.p50Ms as number) - r.targetMs,
    }))
    .sort((a, b) => b.overMs - a.overMs);

  if (!endToEnd.count) {
    return { p50Ms: null, p95Ms: null, targetMs, samples: 0, met: null, overMs: null, offenders };
  }
  return {
    p50Ms: endToEnd.p50_ms,
    p95Ms: endToEnd.p95_ms,
    targetMs,
    samples: endToEnd.count,
    met: endToEnd.p50_ms <= targetMs,
    overMs: Math.max(0, endToEnd.p50_ms - targetMs),
    offenders,
  };
}

/** The mouth-to-ear split by capture mode: the realtime call route beside the push-to-talk tract. */
export function byMode(voice: VoiceStages | null, asr: string): DurStat {
  const found = voice?.mouth_to_ear_by_asr?.find((r) => r.asr === asr);
  if (!found || !found.count) return EMPTY;
  return { count: found.count, p50_ms: found.p50_ms, p95_ms: found.p95_ms, max_ms: found.p95_ms };
}

/** `123 / 456 ms`, or an em dash when the window held no sample of this stage. */
export function formatStat(p50: number | null, p95: number | null): string {
  if (p50 === null || p95 === null) return '—';
  return `${Math.round(p50)} / ${Math.round(p95)} ms`;
}

/** How far along its own budget a row sits, clamped to [0,1] for a bar. */
export function budgetFill(row: BudgetRow): number {
  if (row.p50Ms === null || !row.targetMs) return 0;
  return Math.max(0, Math.min(1, row.p50Ms / row.targetMs));
}

/** ok / warn / bad, by how far past its budget a row landed. Shared by the bar and the row tone. */
export function budgetSeverity(row: BudgetRow): 'ok' | 'warn' | 'bad' | 'none' {
  if (row.p50Ms === null) return 'none';
  if (row.p50Ms <= row.targetMs) return 'ok';
  return row.p50Ms > row.targetMs * 2 ? 'bad' : 'warn';
}
